import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Badge,
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  PrecisionManufacturing as MachineIcon,
  SearchOutlined,
  PublicOutlined,
  LockOutlined,
  GridView as CardViewIcon,
  ViewStreamOutlined as CompactViewIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import useTranslation from "../../../shared/hooks/useTranslation";
import { ServiceRequest, ServiceRequestSortParameter, ServiceRequestStatusCode, ServiceRequestsState } from "../models/models";
import { createStatusLabelMap } from "../utils/statusMaps";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  getServiceRequests,
  changeServiceRequestsQuery,
  getServiceRequestsFilters,
  publishServiceRequest,
  unpublishServiceRequest,
  archiveServiceRequest,
  unarchiveServiceRequest,
  reactivateServiceRequest,
  deleteServiceRequest,
  clearError
} from "../customerServicingSlice";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import Loader from "../../../shared/components/Loader";
import TableEmpty from "../../../shared/components/tables/TableEmpty";
import LightTablePagination from "../../../shared/components/tables/LightTablePagination";
import BoxSearchIcon from "../../../shared/assets/icons/BoxSearchIcon";
import ActionToast from "../../../shared/components/ActionToast";
import { NotificationMessage } from "../../../shared/models/models";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import serviceRequestsApi from "../api/serviceRequestsApi";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { Modules } from "../../../core/models/PermissionModels";
import { PermissionLevel } from "../../../core/models/PermissionModels";
import { dt, paginationWrapperSx, StatusBadgeColorConfig, accentGradientDark, accentGradientDarkHover } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import RedesignStatusBadge from "../../../shared/components/redesign/RedesignStatusBadge";
import RedesignFilterItem from "../../../shared/components/redesign/RedesignFilterItem";
import useDataRefresh from "../../../shared/notifications/hooks/useDataRefresh";
import useOfferPresence from "../../../shared/notifications/hooks/useOfferPresence";
import useVisibilityRefresh from "../../../shared/hooks/useVisibilityRefresh";
import useChangeHighlight from "../../../shared/hooks/useChangeHighlight";
import ServiceRequestsActionStrip, { SR_STRIP_BADGE_MAP } from "./ServiceRequestsActionStrip";
import ServiceLifecycleTracker from "../../shared/components/ServiceLifecycleTracker";
import ArchiveViewToggle from "../../shared/components/ArchiveViewToggle";
import { getOwnerLifecycleStages } from "../../shared/utils/lifecycleStages";
import ServiceRequestCard from "./ServiceRequestCard";
import ServiceRequestCardCompact from "./ServiceRequestCardCompact";
import ConfirmCompletionDialog from "./ConfirmCompletionDialog";
import LeaveReviewDialog from "./LeaveReviewDialog";
import CompareOffersDialog from "./CompareOffersDialog";
import PublishServiceRequestDialog from "./PublishServiceRequestDialog";
import RequestRevisionDialog from "./RequestRevisionDialog";
import serviceExecutionApi from "../api/serviceExecutionApi";
import { ServiceCompletionDetails } from "../models/executionModels";
import { createReview } from "../serviceReviewsSlice";

// ═══════════════════════════════════════════════════════════════
//  STATUS COLORS
// ═══════════════════════════════════════════════════════════════

const statusBadgeColors: Record<number, StatusBadgeColorConfig> = {
  [ServiceRequestStatusCode.draft]: { bg: "#F9EBEA", text: "#C0392B", border: "#F5B7B1" },
  [ServiceRequestStatusCode.openForOffers]: { bg: "#FEF9E7", text: "#F39C12", border: "#F9E79F" },
  [ServiceRequestStatusCode.negotiating]: { bg: "#F5EEF8", text: "#8E44AD", border: "#D2B4DE" },
  [ServiceRequestStatusCode.inProgress]: { bg: "#EBF5FB", text: dt.blueHover, border: "#AED6F1" },
  [ServiceRequestStatusCode.completed]: { bg: "#EAFAF1", text: "#27AE60", border: "#ABEBC6" },
  [ServiceRequestStatusCode.archived]: { bg: "#F4F6F9", text: "#6B7280", border: "#D1D5DB" },
  [ServiceRequestStatusCode.awaitingProviderConfirmation]: { bg: "#FEF9E7", text: "#F39C12", border: "#F9E79F" },
  [ServiceRequestStatusCode.awaitingCompletionConfirmation]: { bg: "#FDF2E9", text: "#E67E22", border: "#F5CBA7" },
};

// ═══════════════════════════════════════════════════════════════
//  OffersBadge
// ═══════════════════════════════════════════════════════════════

interface OffersBadgeProps {
  offersCount: number;
  newOffersCount: number;
  onClick: () => void;
}

const OffersBadge = ({ offersCount, newOffersCount, onClick }: OffersBadgeProps) => {
  const hasOffers = offersCount > 0;
  const hasNew = newOffersCount > 0;

  return (
    <Box
      onClick={hasOffers ? onClick : undefined}
      sx={{
        position: "relative",
        width: "34px",
        height: "34px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12px",
        fontWeight: 700,
        fontFamily: dt.fontMono,
        cursor: hasOffers ? "pointer" : "default",
        border: `2px solid ${hasOffers ? dt.blue : dt.cardBorder}`,
        color: hasOffers ? dt.blue : dt.textMuted,
        backgroundColor: hasOffers ? dt.blueSoft : "transparent",
        transition: "all 0.3s ease",
        boxShadow: hasNew ? `0 0 0 3px ${dt.blueGlow}` : "none",
        animation: hasNew ? "offerRing 2.5s ease-in-out infinite" : "none",
        "&:hover": hasOffers
          ? {
              transform: "scale(1.12)",
              borderColor: dt.blueHover,
              boxShadow: `0 0 0 4px ${dt.blueGlow}`,
            }
          : {},
      }}
    >
      {offersCount}
      {hasNew && (
        <Box
          sx={{
            position: "absolute",
            top: "-3px",
            right: "-3px",
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            backgroundColor: "#E74C3C",
            border: `2px solid ${dt.cardBg}`,
            boxShadow: "0 0 4px rgba(231, 76, 60, 0.4)",
          }}
        />
      )}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
//  MobileRequestCard
// ═══════════════════════════════════════════════════════════════

interface MobileRequestCardProps {
  item: ServiceRequest;
  translate: (key: string, opts?: any) => any;
  onView: (item: ServiceRequest) => void;
  index: number;
  preparingCount?: number;
}

const MobileRequestCard = ({ item, translate, onView, index, preparingCount = 0 }: MobileRequestCardProps) => {
  const navigate = useNavigate();
  const c = statusBadgeColors[item.statusCode] || statusBadgeColors[ServiceRequestStatusCode.archived];

  return (
    <Box
      onClick={() => onView(item)}
      sx={{
        backgroundColor: dt.cardBg,
        borderRadius: dt.radiusSm,
        border: `1px solid ${dt.cardBorder}`,
        borderLeft: `3px solid ${c.text}`,
        padding: "12px",
        cursor: "pointer",
        boxShadow: dt.cardShadow,
        overflow: "hidden",
        width: "100%",
        boxSizing: "border-box",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.06}s both`,
        "@keyframes cardReveal": {
          "0%": { opacity: 0, transform: "translateY(16px) scale(0.98)" },
          "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
        },
        "&:hover": {
          boxShadow: dt.cardShadowHover,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Header: Request ID + Visibility icon + Status */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 1, gap: 0.5, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <Box sx={{ color: dt.textLink, fontWeight: 600, fontSize: "12px", fontFamily: dt.fontMono, letterSpacing: "0.3px" }}>
            {item.internalNumber}
          </Box>
          {item.visibilityCode === 2 ? (
            <LockOutlined sx={{ fontSize: "14px", color: "#27AE60" }} />
          ) : (
            <PublicOutlined sx={{ fontSize: "14px", color: dt.textMuted }} />
          )}
        </Box>
        <RedesignStatusBadge
          label={createStatusLabelMap(translate)[item.statusCode] || ""}
          colorConfig={statusBadgeColors[item.statusCode] || statusBadgeColors[ServiceRequestStatusCode.archived]}
        />
      </Box>
      {/* Lifecycle progress dots */}
      {item.statusCode !== ServiceRequestStatusCode.archived && (
        <Box sx={{ mb: 0.5 }}>
          <ServiceLifecycleTracker stages={getOwnerLifecycleStages(item.statusCode)} variant="compact" />
        </Box>
      )}
      {/* Body */}
      <Box sx={{ mb: 1, overflow: "hidden" }}>
        <Box sx={{ color: dt.textPrimary, fontWeight: 500, fontSize: "13px", fontFamily: dt.font, mb: 0.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.issue}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", color: dt.textSecondary, fontSize: "12px", overflow: "hidden" }}>
          <MachineIcon sx={{ fontSize: "13px", color: dt.textMuted, flexShrink: 0 }} />
          <Box
            component="span"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); item.machine?.id && navigate(`/machinery/${item.machine.id}`); }}
            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: dt.textLink, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
          >
            {item.machine?.name}
          </Box>
        </Box>
      </Box>
      {/* Footer */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", pt: 1, borderTop: `1px solid ${dt.rowBorder}`, gap: 0.5 }}>
        <Box sx={{ color: dt.textMuted, fontSize: "11px", fontFamily: dt.fontMono, letterSpacing: "0.2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {item.openDate ? dayjs(item.openDate).format("DD MMM YYYY") : ""}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          {preparingCount > 0 && (
            <Box
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "10px",
                fontWeight: 600,
                color: "#D97706",
                backgroundColor: "rgba(217, 119, 6, 0.08)",
                padding: "1px 6px",
                borderRadius: "8px",
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: "3px",
                animation: "presencePulse 2s ease-in-out infinite",
                "@keyframes presencePulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.6 },
                },
              }}
            >
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  backgroundColor: "#D97706",
                }}
              />
              {translate("serviceOffers:preparingOffers", { count: preparingCount })}
            </Box>
          )}
          {(item.offersCount ?? 0) > 0 && (
            <OffersBadge offersCount={item.offersCount ?? 0} newOffersCount={item.newOffersCount ?? 0} onClick={() => onView(item)} />
          )}
          <ChevronRightIcon sx={{ color: dt.textMuted, fontSize: "18px" }} />
        </Box>
      </Box>
    </Box>
  );
};


// ═══════════════════════════════════════════════════════════════

const allActiveStatusCodes = [
  ServiceRequestStatusCode.draft,
  ServiceRequestStatusCode.openForOffers,
  ServiceRequestStatusCode.negotiating,
  ServiceRequestStatusCode.awaitingProviderConfirmation,
  ServiceRequestStatusCode.awaitingCompletionConfirmation,
  ServiceRequestStatusCode.inProgress,
  ServiceRequestStatusCode.completed,
];

// ═══════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const MyServiceRequests = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { translate } = useTranslation(["myMachinery", "common", "servicing", "serviceOffers"]);
  const { serviceRequestsQuery, serviceRequests, serviceRequestsFilters, loading, error } =
    useAppSelector<ServiceRequestsState>((state: any) => state.customerServicing);
  const dispatch = useAppDispatch();
  const confirm = useConfirmation();
  const presenceCounts = useOfferPresence();

  // 3-tier responsive: Desktop > 1200, Tablet 768–1200, Phone < 768
  const isCompact = useMediaQuery("(max-width:1200px)");
  const isPhone = useMediaQuery("(max-width:768px)");

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState<string>(serviceRequestsQuery.search || "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);

  // Action strip: track which badge key is active (null = "All" / no strip filter)
  const [stripFilterKey, setStripFilterKey] = useState<string | null>(null);

  // Card/Table view toggle (cards default on desktop, phone always uses MobileRequestCard)
  const [viewMode, setViewMode] = useState<"cards" | "compact">("cards");

  // Archive view toggle: "active" shows all non-archived, "archived" shows only archived
  const [archiveViewMode, setArchiveViewMode] = useState<"active" | "archived">("active");

  // BUG 3: Confirm Completion and Leave Review dialogs (open from card CTA)
  const [confirmCompletionSR, setConfirmCompletionSR] = useState<ServiceRequest | null>(null);
  const [completionData, setCompletionData] = useState<ServiceCompletionDetails | null>(null);
  const [completionLoading, setCompletionLoading] = useState(false);
  const [reviewSR, setReviewSR] = useState<ServiceRequest | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | undefined>(undefined);

  // Request Revision dialog state
  const [revisionSR, setRevisionSR] = useState<ServiceRequest | null>(null);
  const [revisionLoading, setRevisionLoading] = useState(false);

  // Compare Offers dialog state
  const [compareOffersSR, setCompareOffersSR] = useState<ServiceRequest | null>(null);

  // Publish dialog state (Task 2: public/private visibility selection)
  const [publishSR, setPublishSR] = useState<ServiceRequest | null>(null);

  // Post-publish info banner — shown after publishing via stepper (?published=true) or in-page dialog
  const [showPublishedBanner, setShowPublishedBanner] = useState(() => searchParams.get("published") === "true");

  useRedesignSetup();

  // Change highlight: track which service requests changed on silent refresh
  const changedIds = useChangeHighlight(
    serviceRequests?.items ?? [],
    (item) => item.id,
    (item) => `${item.statusCode}-${item.offersCount ?? 0}`,
  );

  // Helper: override pageSize to fetch ALL items from the API (client-side pagination)
  const apiQuery = (query: typeof serviceRequestsQuery) => ({ ...query, pageSize: 9999 });

  // Real-time: silently refresh service requests when DataRefresh arrives
  useDataRefresh("myServiceRequests", () => {
    dispatch(getServiceRequests(apiQuery(serviceRequestsQuery)));
    dispatch(getServiceRequestsFilters({ search: serviceRequestsQuery.search }));
  });
  useVisibilityRefresh(() => {
    dispatch(getServiceRequests(apiQuery(serviceRequestsQuery)));
    dispatch(getServiceRequestsFilters({ search: serviceRequestsQuery.search }));
  });

  // --- Status label map ---
  const statusLabelMap = useMemo(() => createStatusLabelMap(translate), [translate]);

  // --- Filter data ---
  const { activeFilters, archiveFilter } = useMemo(() => {
    const filters = serviceRequestsFilters ?? { statusCodes: [] };
    const activeFilters = allActiveStatusCodes.map(statusCode => {
      const existingFilter = filters.statusCodes.find(f => f.id === statusCode);
      return existingFilter || { id: statusCode, name: statusLabelMap[statusCode], value: 0 };
    });
    const archiveFilter = filters.statusCodes.find(f => f.id === ServiceRequestStatusCode.archived) || {
      id: ServiceRequestStatusCode.archived,
      name: statusLabelMap[ServiceRequestStatusCode.archived],
      value: 0,
    };
    return { activeFilters, archiveFilter };
  }, [serviceRequestsFilters, statusLabelMap]);

  const activeTotal = useMemo(
    () => activeFilters.reduce((sum, f) => sum + f.value, 0),
    [activeFilters]
  );

  // ═══════════════════════════════════════════════════════════
  //  EVENT HANDLERS — unchanged
  // ═══════════════════════════════════════════════════════════

  useEffect(() => {
    const query = {
      ...serviceRequestsQuery,
      statusCodes: [...allActiveStatusCodes], // Exclude archived by default — users must explicitly enable it
      pageNo: 1,
      sortBy: ServiceRequestSortParameter.createdDate,
      sortDesc: true,
    };
    dispatch(changeServiceRequestsQuery(query));
    dispatch(getServiceRequests(apiQuery(query)));
    dispatch(getServiceRequestsFilters({ search: query.search }));
  }, []);

  const handleSort = (parameter: number) => {
    const currentSortBy = serviceRequestsQuery.sortBy;
    const currentSortDesc = serviceRequestsQuery.sortDesc ?? false;
    const sortDesc = currentSortBy !== parameter ? false : !currentSortDesc;
    const newQuery = {
      ...serviceRequestsQuery,
      sortBy: parameter as ServiceRequestSortParameter,
      sortDesc: sortDesc,
      pageNo: 1,
    };
    dispatch(changeServiceRequestsQuery(newQuery));
    dispatch(getServiceRequests(apiQuery(newQuery)));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    const newQuery = { ...serviceRequestsQuery, pageNo: 1, pageSize: newPageSize };
    dispatch(changeServiceRequestsQuery(newQuery));
    // No API call needed — we already have all items, pagination is client-side
  };

  const handlePageNoChange = (newPage: number) => {
    const newQuery = { ...serviceRequestsQuery, pageNo: newPage };
    dispatch(changeServiceRequestsQuery(newQuery));
    // No API call needed — we already have all items, pagination is client-side
  };

  const handleSearch = (value?: string) => {
    const newQuery = { ...serviceRequestsQuery, search: value, pageNo: 1 };
    dispatch(changeServiceRequestsQuery(newQuery));
    dispatch(getServiceRequests(apiQuery(newQuery)));
    dispatch(getServiceRequestsFilters({ search: value }));
  };

  const handleFiltersChange = (selectedStatusCodes: number[]) => {
    // When computing the API query, always respect archive view mode:
    // - In "active" mode: ensure archived is excluded
    // - In "archived" mode: only query archived
    const effectiveCodes = archiveViewMode === "archived"
      ? [ServiceRequestStatusCode.archived]
      : selectedStatusCodes.length > 0
        ? selectedStatusCodes.filter(c => c !== ServiceRequestStatusCode.archived)
        : [...allActiveStatusCodes];
    const newQuery = {
      ...serviceRequestsQuery,
      statusCodes: effectiveCodes,
      pageNo: 1,
    };
    dispatch(changeServiceRequestsQuery(newQuery));
    dispatch(getServiceRequests(apiQuery(newQuery)));
    dispatch(getServiceRequestsFilters({ search: newQuery.search }));
  };

  // Handle archive view toggle
  const handleArchiveViewChange = (view: "active" | "archived") => {
    setArchiveViewMode(view);
    setStripFilterKey(null); // Clear action strip filter
    if (view === "archived") {
      // Switch to archive: query only archived items
      const newQuery = {
        ...serviceRequestsQuery,
        statusCodes: [ServiceRequestStatusCode.archived],
        pageNo: 1,
      };
      dispatch(changeServiceRequestsQuery(newQuery));
      dispatch(getServiceRequests(apiQuery(newQuery)));
      dispatch(getServiceRequestsFilters({ search: newQuery.search }));
    } else {
      // Switch to active: query all non-archived items
      const newQuery = {
        ...serviceRequestsQuery,
        statusCodes: [...allActiveStatusCodes],
        pageNo: 1,
      };
      dispatch(changeServiceRequestsQuery(newQuery));
      dispatch(getServiceRequests(apiQuery(newQuery)));
      dispatch(getServiceRequestsFilters({ search: newQuery.search }));
    }
  };

  // Handle strip badge click — toggle: click active badge deselects it
  const handleStripBadgeClick = (badgeKey: string) => {
    // If in archive view, switch back to active first
    if (archiveViewMode === "archived") {
      setArchiveViewMode("active");
    }
    if (stripFilterKey === badgeKey) {
      // Already active — deselect (show all)
      setStripFilterKey(null);
      // Must pass allActiveStatusCodes since we're switching from archive view potentially
      const newQuery = {
        ...serviceRequestsQuery,
        statusCodes: [...allActiveStatusCodes],
        pageNo: 1,
      };
      dispatch(changeServiceRequestsQuery(newQuery));
      dispatch(getServiceRequests(apiQuery(newQuery)));
      dispatch(getServiceRequestsFilters({ search: newQuery.search }));
    } else {
      setStripFilterKey(badgeKey);
      const statusCodes = SR_STRIP_BADGE_MAP[badgeKey] || [];
      const newQuery = {
        ...serviceRequestsQuery,
        statusCodes: statusCodes.length > 0 ? statusCodes : [...allActiveStatusCodes],
        pageNo: 1,
      };
      dispatch(changeServiceRequestsQuery(newQuery));
      dispatch(getServiceRequests(apiQuery(newQuery)));
      dispatch(getServiceRequestsFilters({ search: newQuery.search }));
    }
  };

  // Handle "All" badge click — always clears filter
  const handleStripAllClick = () => {
    // If in archive view, switch back to active
    if (archiveViewMode === "archived") {
      setArchiveViewMode("active");
    }
    setStripFilterKey(null);
    const newQuery = {
      ...serviceRequestsQuery,
      statusCodes: [...allActiveStatusCodes],
      pageNo: 1,
    };
    dispatch(changeServiceRequestsQuery(newQuery));
    dispatch(getServiceRequests(apiQuery(newQuery)));
    dispatch(getServiceRequestsFilters({ search: newQuery.search }));
  };

  const handleFilterToggle = useCallback(
    (statusCode: number) => {
      // If in archive view and user clicks a status filter, switch to active view
      if (archiveViewMode === "archived") {
        setArchiveViewMode("active");
        // Start with the clicked status code selected
        const newQuery = {
          ...serviceRequestsQuery,
          statusCodes: [statusCode],
          pageNo: 1,
        };
        dispatch(changeServiceRequestsQuery(newQuery));
        dispatch(getServiceRequests(apiQuery(newQuery)));
        dispatch(getServiceRequestsFilters({ search: newQuery.search }));
        return;
      }
      const current = serviceRequestsQuery.statusCodes || [];
      const next = current.includes(statusCode)
        ? current.filter((c: number) => c !== statusCode)
        : [...current, statusCode];
      handleFiltersChange(next);
    },
    [serviceRequestsQuery.statusCodes, handleFiltersChange, archiveViewMode]
  );

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (value.trim().length === 0) {
        handleSearch(undefined);
        return;
      }
      debounceRef.current = setTimeout(() => {
        handleSearch(value || undefined);
      }, 400);
    },
    [handleSearch]
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        handleSearch(searchValue || undefined);
      }
    },
    [searchValue, handleSearch]
  );

  const handleDateFromChange = (value: Dayjs | null) => {
    const newQuery = { ...serviceRequestsQuery, dateFrom: value?.toISOString(), pageNo: 1 };
    dispatch(changeServiceRequestsQuery(newQuery));
    dispatch(getServiceRequests(apiQuery(newQuery)));
    dispatch(getServiceRequestsFilters({ search: newQuery.search }));
  };

  const handleDateToChange = (value: Dayjs | null) => {
    const newQuery = { ...serviceRequestsQuery, dateTo: value?.toISOString(), pageNo: 1 };
    dispatch(changeServiceRequestsQuery(newQuery));
    dispatch(getServiceRequests(apiQuery(newQuery)));
    dispatch(getServiceRequestsFilters({ search: newQuery.search }));
  };

  const handlePublish = async (serviceRequest: ServiceRequest) => {
    // Open the publish dialog instead of directly publishing
    setPublishSR(serviceRequest);
  };

  const handlePublishConfirm = async (visibilityCode: number, targetPartnerIds: string[]) => {
    if (!publishSR) return;
    await dispatch(publishServiceRequest({ id: publishSR.id, visibilityCode, targetPartnerIds }));
    await dispatch(getServiceRequests(apiQuery(serviceRequestsQuery)));
    await dispatch(getServiceRequestsFilters({ search: serviceRequestsQuery.search }));
    setShowPublishedBanner(true);
  };

  const handleDismissPublishedBanner = () => {
    setShowPublishedBanner(false);
    // Clear the URL param if present
    if (searchParams.get("published")) {
      searchParams.delete("published");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const handleUnpublish = async (serviceRequest: ServiceRequest) => {
    await dispatch(unpublishServiceRequest(serviceRequest.id));
    await dispatch(getServiceRequestsFilters({ search: serviceRequestsQuery.search }));
    await dispatch(getServiceRequests(apiQuery(serviceRequestsQuery)));
  };

  const handleView = (serviceRequest: ServiceRequest) => {
    navigate(`/service-board/${serviceRequest.id}`);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-EU", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleDownloadPdf = (serviceRequest: ServiceRequest) => {
    serviceRequestsApi.downloadServiceRequestPdf(serviceRequest);
  };

  const handleEdit = (serviceRequest: ServiceRequest) => {
    navigate(`/service-requests/edit/${serviceRequest.id}`);
  };

  const handleDelete = (serviceRequest: ServiceRequest) => {
    confirm({
      title: translate("myMachinery:deleteServiceRequestTitle"),
      text: translate("myMachinery:deleteServiceRequestText", { number: serviceRequest.internalNumber }),
      confirmButton: translate("myMachinery:yesDelete"),
      closeButton: translate("common:common.no"),
      severity: "error",
      onConfirm: async () => {
        await dispatch(deleteServiceRequest(serviceRequest.id));
        await dispatch(getServiceRequests(apiQuery(serviceRequestsQuery)));
      },
    });
  };

  const handleArchive = async (serviceRequest: ServiceRequest) => {
    await dispatch(archiveServiceRequest(serviceRequest.id));
    await dispatch(getServiceRequests(apiQuery(serviceRequestsQuery)));
    await dispatch(getServiceRequestsFilters({ search: serviceRequestsQuery.search }));
  };

  const handleUnarchive = async (serviceRequest: ServiceRequest) => {
    await dispatch(unarchiveServiceRequest(serviceRequest.id));
    await dispatch(getServiceRequests(apiQuery(serviceRequestsQuery)));
    await dispatch(getServiceRequestsFilters({ search: serviceRequestsQuery.search }));
  };

  const handleReactivate = async (serviceRequest: ServiceRequest) => {
    await dispatch(reactivateServiceRequest(serviceRequest.id));
    await dispatch(getServiceRequests(apiQuery(serviceRequestsQuery)));
    await dispatch(getServiceRequestsFilters({ search: serviceRequestsQuery.search }));
  };

  // ═══════════════════════════════════════════════════════════
  //  CONFIRM COMPLETION + REVIEW DIALOG HANDLERS
  // ═══════════════════════════════════════════════════════════

  const handleOpenConfirmCompletion = async (sr: ServiceRequest) => {
    setConfirmCompletionSR(sr);
    setCompletionLoading(true);
    try {
      const data = await serviceExecutionApi.getCompletion(sr.id);
      setCompletionData(data);
    } catch {
      setCompletionData(null);
    } finally {
      setCompletionLoading(false);
    }
  };

  const handleConfirmCompletion = async () => {
    if (!confirmCompletionSR) return;
    setCompletionLoading(true);
    try {
      await serviceExecutionApi.confirmCompletion(confirmCompletionSR.id);
      setConfirmCompletionSR(null);
      setCompletionData(null);
      // Refresh the list
      dispatch(getServiceRequests(apiQuery(serviceRequestsQuery)));
      dispatch(getServiceRequestsFilters({ search: serviceRequestsQuery.search }));
      // Chain into review dialog
      setReviewSR(confirmCompletionSR);
    } finally {
      setCompletionLoading(false);
    }
  };

  const handleRequestRevision = () => {
    // Close completion dialog and open revision dialog directly
    if (confirmCompletionSR) {
      setRevisionSR(confirmCompletionSR);
    }
    setConfirmCompletionSR(null);
    setCompletionData(null);
  };

  const handleSubmitRevision = async (reason: string) => {
    if (!revisionSR) return;
    setRevisionLoading(true);
    try {
      await serviceExecutionApi.requestRevision(revisionSR.id, { reason });
      setRevisionSR(null);
      // Refresh the list
      dispatch(getServiceRequests(apiQuery(serviceRequestsQuery)));
      dispatch(getServiceRequestsFilters({ search: serviceRequestsQuery.search }));
    } finally {
      setRevisionLoading(false);
    }
  };

  const handleOpenLeaveReview = (sr: ServiceRequest) => {
    setReviewSR(sr);
  };

  const handleSubmitReview = async (rating: number, title?: string, comment?: string) => {
    if (!reviewSR || !reviewSR.acceptedOffer) return;
    setReviewLoading(true);
    setReviewError(undefined);
    try {
      await dispatch(createReview({
        serviceRequestId: reviewSR.id,
        offerId: reviewSR.acceptedOffer.offerId,
        rating,
        title,
        comment,
      })).unwrap();
      setReviewSR(null);
      // Refresh the list
      dispatch(getServiceRequests(apiQuery(serviceRequestsQuery)));
      dispatch(getServiceRequestsFilters({ search: serviceRequestsQuery.search }));
    } catch (err: any) {
      setReviewError(err?.message || "Failed to submit review");
    } finally {
      setReviewLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════
  //  CLIENT-SIDE PAGINATION
  //  All items are fetched from API (pageSize: 9999).
  //  We slice locally based on the user's display pageSize/pageNo.
  // ═══════════════════════════════════════════════════════════

  const allItems = serviceRequests?.items || [];
  const displayPageSize = serviceRequestsQuery.pageSize ?? 10;
  const displayPageNo = serviceRequestsQuery.pageNo ?? 1;
  const startIdx = (displayPageNo - 1) * displayPageSize;
  const pageItems = allItems.slice(startIdx, startIdx + displayPageSize);

  if (loading && !serviceRequests) {
    return <Loader />;
  }

  const activeFilterCount = serviceRequestsQuery.statusCodes?.length ?? 0;

  // ═══════════════════════════════════════════════════════════
  //  Shared: active group toggle handler
  // ═══════════════════════════════════════════════════════════

  const handleActiveGroupToggle = () => {
    // If in archive view, switch to active view
    if (archiveViewMode === "archived") {
      setArchiveViewMode("active");
      const newQuery = {
        ...serviceRequestsQuery,
        statusCodes: [...allActiveStatusCodes],
        pageNo: 1,
      };
      dispatch(changeServiceRequestsQuery(newQuery));
      dispatch(getServiceRequests(apiQuery(newQuery)));
      dispatch(getServiceRequestsFilters({ search: newQuery.search }));
      return;
    }
    const activeCodes = activeFilters.map((f) => f.id);
    const allSelected = activeCodes.every((c: number) =>
      (serviceRequestsQuery.statusCodes || []).includes(c)
    );
    if (allSelected) {
      handleFiltersChange(
        (serviceRequestsQuery.statusCodes || []).filter(
          (c: number) => !activeCodes.includes(c)
        )
      );
    } else {
      const combined = [...(serviceRequestsQuery.statusCodes || [])];
      activeCodes.forEach((c: number) => {
        if (!combined.includes(c)) combined.push(c);
      });
      handleFiltersChange(combined);
    }
  };

  // ═══════════════════════════════════════════════════════════
  //  Shared: inline filter content (used in sidebar & accordion)
  // ═══════════════════════════════════════════════════════════

  const renderFilterContent = () => (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <Box sx={{ flexShrink: 0 }}>
      {/* Search input */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          background: dt.inputBg,
          borderRadius: dt.radiusSm,
          border: `1.5px solid ${searchFocused ? dt.blue : "transparent"}`,
          padding: "0 12px",
          height: "40px",
          transition: "all 0.25s ease",
          boxShadow: searchFocused ? `0 0 0 3px ${dt.blueGlow}` : "none",
          mb: "16px",
        }}
      >
        <SearchOutlined
          sx={{ fontSize: "16px", color: searchFocused ? dt.blue : dt.textMuted, flexShrink: 0, transition: "color 0.2s" }}
        />
        <input
          type="text"
          value={searchValue}
          onChange={handleSearchInputChange}
          onKeyDown={handleSearchKeyDown}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          placeholder={translate("common:buttons.search") + "..."}
          style={{
            border: "none",
            outline: "none",
            width: "100%",
            fontSize: "13px",
            fontFamily: 'var(--font-sans)',
            color: dt.textPrimary,
            background: "transparent",
            marginLeft: "8px",
          }}
        />
      </Box>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", "&::-webkit-scrollbar": { width: "5px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#D1D5DB", borderRadius: "10px", "&:hover": { background: "#9CA3AF" } }, scrollbarWidth: "thin" as const, scrollbarColor: "#D1D5DB transparent" }}>

      {/* Date Range */}
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "1.5px",
          color: dt.textMuted,
          fontFamily: dt.font,
          mb: "10px",
          px: "4px",
        }}
      >
        {translate("dateRange")}
      </Box>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, px: "4px", mb: "4px" }}>
          <DatePicker
            label={translate("from")}
            value={serviceRequestsQuery.dateFrom ? dayjs(serviceRequestsQuery.dateFrom) : null}
            onChange={handleDateFromChange}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                sx: {
                  "& .MuiOutlinedInput-root": {
                    borderRadius: dt.radiusSm,
                    fontFamily: dt.font,
                    fontSize: "13px",
                    backgroundColor: dt.inputBg,
                    "& fieldset": { borderColor: "transparent" },
                    "&:hover fieldset": { borderColor: dt.cardBorder },
                    "&.Mui-focused fieldset": { borderColor: dt.blue, borderWidth: "1.5px" },
                  },
                  "& .MuiInputLabel-root": { fontFamily: dt.font, fontSize: "13px" },
                },
              },
            }}
          />
          <DatePicker
            label={translate("to")}
            value={serviceRequestsQuery.dateTo ? dayjs(serviceRequestsQuery.dateTo) : null}
            onChange={handleDateToChange}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                sx: {
                  "& .MuiOutlinedInput-root": {
                    borderRadius: dt.radiusSm,
                    fontFamily: dt.font,
                    fontSize: "13px",
                    backgroundColor: dt.inputBg,
                    "& fieldset": { borderColor: "transparent" },
                    "&:hover fieldset": { borderColor: dt.cardBorder },
                    "&.Mui-focused fieldset": { borderColor: dt.blue, borderWidth: "1.5px" },
                  },
                  "& .MuiInputLabel-root": { fontFamily: dt.font, fontSize: "13px" },
                },
              },
            }}
          />
        </Box>
      </LocalizationProvider>

      {/* Divider */}
      <Box sx={{ borderTop: `1px solid ${dt.cardBorder}`, mb: "16px" }} />

      {/* Filter header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mb: "12px", px: "4px" }}>
        <Box sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px", color: dt.textMuted, fontFamily: dt.font }}>
          {translate("filterBy")}
        </Box>
        {((serviceRequestsQuery.statusCodes?.length ?? 0) > 0 || serviceRequestsQuery.dateFrom || serviceRequestsQuery.dateTo) && (
          <Box
            onClick={() => {
              handleFiltersChange([]);
              handleDateFromChange(null);
              handleDateToChange(null);
            }}
            sx={{ fontSize: "12px", color: dt.blue, cursor: "pointer", fontWeight: 500, fontFamily: dt.font, "&:hover": { textDecoration: "underline" } }}
          >
            {translate("clearAll")}
          </Box>
        )}
      </Box>

      {/* Active requests group */}
      <RedesignFilterItem
        label={translate("activeRequests")}
        count={activeTotal}
        isSelected={activeFilters.length > 0 && activeFilters.every((f) => (serviceRequestsQuery.statusCodes || []).includes(f.id))}
        onClick={handleActiveGroupToggle}
      />

      {/* Individual status filters */}
      {activeFilters.map((filter) => (
        <RedesignFilterItem
          key={filter.id}
          label={statusLabelMap[filter.id]}
          count={filter.value}
          isSelected={(serviceRequestsQuery.statusCodes || []).includes(filter.id)}
          statusColor={statusBadgeColors[filter.id]?.text}
          indent
          onClick={() => handleFilterToggle(filter.id)}
        />
      ))}

      </Box>
    </Box>
  );

  // ═══════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        backgroundColor: dt.pageBg,
        backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.pageDotColor} 1px, transparent 0)`,
        backgroundSize: "28px 28px",
        fontFamily: dt.font,
        overflowX: "hidden",
        px: { xs: "16px", md: "32px" },
        "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
      }}
    >
      <ActionToast
        open={!!error}
        variant="error"
        message={translate(NotificationMessage.error(error).message)}
        onClose={() => dispatch(clearError())}
        duration={6000}
      />

      {/* ═══════════════════════════════════════════════════════
          MAIN CONTENT
          ═══════════════════════════════════════════════════════ */}
      <Box
        sx={{
          display: "flex",
          gap: "24px",
          pt: { xs: 2, md: 3 },
          pb: 3,
          minWidth: 0,
          height: "calc(100vh - 110px)",
          alignItems: "stretch",
          overflow: "hidden",
        }}
      >
        {/* ═════════════════════════════════════════════════════
            DESKTOP SIDEBAR (>1200px only)
            ═════════════════════════════════════════════════════ */}
        {!isCompact && (
          <Box
            sx={{
              width: "260px",
              minWidth: "260px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              animation: "fadeLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: dt.radiusLg,
                border: `1px solid ${dt.cardBorder}`,
                padding: "22px",
                boxShadow: dt.cardShadow,
                height: "100%",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {renderFilterContent()}
            </Box>
          </Box>
        )}

        {/* ═════════════════════════════════════════════════════
            MAIN AREA (table or cards)
            ═════════════════════════════════════════════════════ */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            gap: "16px",
            overflow: "hidden",
            animation: "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
          }}
        >
          {/* ═══════════════════════════════════════════════════
              ACTION STRIP + VIEW TOGGLE + CREATE BUTTON
              ═══════════════════════════════════════════════════ */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {archiveViewMode === "active" ? (
                <ServiceRequestsActionStrip
                  filters={serviceRequestsFilters}
                  activeFilterKey={stripFilterKey}
                  translate={translate}
                  onBadgeClick={handleStripBadgeClick}
                  onAllClick={handleStripAllClick}
                />
              ) : (
                <Box sx={{ fontSize: "14px", fontWeight: 600, color: dt.textSecondary, fontFamily: dt.font }}>
                  {translate("servicing:archivedView")}
                </Box>
              )}
            </Box>
            <ArchiveViewToggle
              activeView={archiveViewMode}
              activeCount={activeTotal}
              archivedCount={archiveFilter.value}
              onViewChange={handleArchiveViewChange}
              activeLabel={translate("servicing:activeView")}
              archivedLabel={translate("servicing:archivedView")}
            />
            {!isPhone && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, val) => val && setViewMode(val)}
                size="small"
                sx={{
                  flexShrink: 0,
                  "& .MuiToggleButton-root": {
                    border: `1px solid ${dt.cardBorder}`,
                    borderRadius: `${dt.radiusSm} !important`,
                    padding: "4px 10px",
                    textTransform: "none",
                    fontFamily: dt.font,
                    fontSize: "12px",
                    fontWeight: 600,
                    color: dt.textSecondary,
                    transition: "all 0.2s ease",
                    "&.Mui-selected": {
                      backgroundColor: dt.blueGlow,
                      color: dt.blue,
                      borderColor: dt.blue,
                      "&:hover": { backgroundColor: dt.blueGlow },
                    },
                    "&:hover": { backgroundColor: dt.blueSoft },
                  },
                }}
              >
                <ToggleButton value="cards">
                  <CardViewIcon sx={{ fontSize: "16px", mr: 0.5 }} />
                  {translate("servicing:card.viewToggle.cards")}
                </ToggleButton>
                <ToggleButton value="compact">
                  <CompactViewIcon sx={{ fontSize: "16px", mr: 0.5 }} />
                  {translate("servicing:card.viewToggle.compact")}
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            {/* Create button — inline with action strip */}
            <PermissionGuard module={Modules.Servicing} level={PermissionLevel.Write}>
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
                onClick={() => navigate("/service-requests/new")}
                sx={{
                  background: accentGradientDark,
                  borderRadius: dt.radiusPill,
                  padding: isPhone ? "6px 14px" : "6px 18px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "12px",
                  fontFamily: dt.font,
                  letterSpacing: "0.2px",
                  boxShadow: `0 2px 8px rgba(52,152,219,0.25)`,
                  border: "none",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  minWidth: "auto",
                  lineHeight: 1.5,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  ...(isPhone && { width: "100%", order: -1 }),
                  "&:hover": {
                    background: accentGradientDarkHover,
                    transform: "translateY(-1px)",
                    boxShadow: `0 4px 12px rgba(52,152,219,0.3)`,
                  },
                  "&:active": {
                    transform: "translateY(0)",
                    boxShadow: `0 2px 8px rgba(52,152,219,0.25)`,
                  },
                }}
              >
                {translate("common:create")}
              </Button>
            </PermissionGuard>
          </Box>

          {/* ═══════════════════════════════════════════════════
              POST-PUBLISH INFO BANNER
              ═══════════════════════════════════════════════════ */}
          {showPublishedBanner && (
            <Alert
              severity="info"
              onClose={handleDismissPublishedBanner}
              closeText={translate("common:buttons.dismiss") || "Dismiss"}
              sx={{
                borderRadius: dt.radiusSm,
                fontFamily: dt.font,
                fontSize: "13px",
                lineHeight: 1.6,
                border: `1px solid ${dt.blue}22`,
                backgroundColor: `${dt.blue}08`,
                animation: "fadeUp 0.4s cubic-bezier(0.4, 0, 0.2, 1) both",
                "& .MuiAlert-message": {
                  fontFamily: dt.font,
                  fontSize: "13px",
                  color: dt.textPrimary,
                },
                "& .MuiAlert-icon": {
                  color: dt.blue,
                },
                "& .MuiAlert-action": {
                  alignItems: "flex-start",
                  pt: "2px",
                },
              }}
            >
              {translate("servicing:postPublishBanner")}
            </Alert>
          )}

          {/* ═══════════════════════════════════════════════════
              ACCORDION FILTERS (tablet & phone — compact mode)
              Service-board pattern: collapsible Accordion
              ═══════════════════════════════════════════════════ */}
          {isCompact && (
            <Accordion
              expanded={filtersExpanded}
              onChange={() => setFiltersExpanded(!filtersExpanded)}
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: `${dt.radiusSm} !important`,
                border: `1px solid ${dt.cardBorder}`,
                boxShadow: "none !important",
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: "0 !important" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: dt.textSecondary }} />}
                sx={{
                  minHeight: "48px",
                  px: 2,
                  "&.Mui-expanded": { minHeight: "48px" },
                  "& .MuiAccordionSummary-content": {
                    alignItems: "center",
                    gap: 1,
                    my: "10px",
                    "&.Mui-expanded": { my: "10px" },
                  },
                }}
              >
                <FilterListIcon sx={{ color: dt.textMuted, fontSize: "20px" }} />
                <Box sx={{ fontSize: "14px", fontWeight: 500, color: dt.textSecondary, fontFamily: dt.font }}>
                  {translate("filters")}
                </Box>
                {activeFilterCount > 0 && (
                  <Badge
                    badgeContent={activeFilterCount}
                    sx={{
                      ml: 1,
                      "& .MuiBadge-badge": {
                        backgroundColor: dt.blue,
                        color: "#fff",
                        fontSize: "10px",
                        fontFamily: dt.fontMono,
                        minWidth: "18px",
                        height: "18px",
                      },
                    }}
                  />
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                {renderFilterContent()}
              </AccordionDetails>
            </Accordion>
          )}

          {/* ═══════════════════════════════════════════════════
              COMPACT CARD VIEW (desktop + tablet, i.e. not phone)
              ═══════════════════════════════════════════════════ */}
          {!isPhone && viewMode === "compact" && (
            <>
              {allItems.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    overflow: "hidden",
                    borderRadius: dt.radiusSm,
                    backgroundColor: dt.cardBg,
                    border: `1px solid ${dt.cardBorder}`,
                    boxShadow: dt.cardShadow,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      overflowX: "hidden",
                      padding: { xs: 1, sm: 1.5, md: 2 },
                      "&::-webkit-scrollbar": { width: "5px" },
                      "&::-webkit-scrollbar-track": { background: "transparent" },
                      "&::-webkit-scrollbar-thumb": { background: "#D1D5DB", borderRadius: "10px", "&:hover": { background: "#9CA3AF" } },
                      scrollbarWidth: "thin" as const,
                      scrollbarColor: "#D1D5DB transparent",
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
                      {pageItems.map((item, index) => (
                        <Box
                          key={item.id}
                          sx={changedIds.has(item.id) ? { animation: "rowHighlight 1.5s ease-out", borderRadius: "8px" } : undefined}
                        >
                          <ServiceRequestCardCompact
                            item={item}
                            index={index}
                            formatCurrency={formatCurrency}
                            onClick={handleView}
                            onPublish={handlePublish}
                            onViewOffers={(sr) => setCompareOffersSR(sr)}
                            onConfirmCompletion={handleOpenConfirmCompletion}
                            onLeaveReview={handleOpenLeaveReview}
                            onArchive={handleArchive}
                            onUnarchive={handleUnarchive}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      borderTop: `1px solid ${dt.cardBorder}`,
                      px: { xs: 1, md: 2 },
                      ...paginationWrapperSx,
                    }}
                  >
                    <LightTablePagination
                      totalCount={allItems.length}
                      currentPage={displayPageNo}
                      pageSize={displayPageSize}
                      pageSizeOptions={[10, 25, 50, 100]}
                      onPageChange={handlePageNoChange}
                      onPageSizeChange={handlePageSizeChange}
                      rowsPerPageLabel={translate("rowsPerPage")}
                      ofLabel={translate("of")}
                    />
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: dt.cardBg,
                    borderRadius: dt.radiusLg,
                    border: `1px solid ${dt.cardBorder}`,
                    boxShadow: dt.cardShadow,
                    minHeight: "400px",
                    flex: 1,
                  }}
                >
                  <TableEmpty
                    iconComponent={BoxSearchIcon}
                    text={translate("noServiceRequestsFound")}
                    secondaryText={translate("createYourFirst")}
                  />
                </Box>
              )}
            </>
          )}

          {/* ═══════════════════════════════════════════════════
              CARD VIEW (desktop + tablet, i.e. not phone)
              ═══════════════════════════════════════════════════ */}
          {!isPhone && viewMode === "cards" && (
            <>
              {allItems.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    overflow: "hidden",
                    borderRadius: dt.radiusSm,
                    backgroundColor: dt.cardBg,
                    border: `1px solid ${dt.cardBorder}`,
                    boxShadow: dt.cardShadow,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      overflowX: "hidden",
                      padding: { xs: 1, sm: 1.5, md: 2 },
                      "&::-webkit-scrollbar": { width: "5px" },
                      "&::-webkit-scrollbar-track": { background: "transparent" },
                      "&::-webkit-scrollbar-thumb": { background: "#D1D5DB", borderRadius: "10px", "&:hover": { background: "#9CA3AF" } },
                      scrollbarWidth: "thin" as const,
                      scrollbarColor: "#D1D5DB transparent",
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%" }}>
                      {pageItems.map((item, index) => (
                        <Box
                          key={item.id}
                          sx={changedIds.has(item.id) ? { animation: "rowHighlight 1.5s ease-out", borderRadius: "8px" } : undefined}
                        >
                          <ServiceRequestCard
                            item={item}
                            index={index}
                            formatCurrency={formatCurrency}
                            onClick={handleView}
                            onPublish={handlePublish}
                            onViewOffers={(sr) => setCompareOffersSR(sr)}
                            onConfirmCompletion={handleOpenConfirmCompletion}
                            onLeaveReview={handleOpenLeaveReview}
                            onArchive={handleArchive}
                            onUnarchive={handleUnarchive}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      borderTop: `1px solid ${dt.cardBorder}`,
                      px: { xs: 1, md: 2 },
                      ...paginationWrapperSx,
                    }}
                  >
                    <LightTablePagination
                      totalCount={allItems.length}
                      currentPage={displayPageNo}
                      pageSize={displayPageSize}
                      pageSizeOptions={[10, 25, 50, 100]}
                      onPageChange={handlePageNoChange}
                      onPageSizeChange={handlePageSizeChange}
                      rowsPerPageLabel={translate("rowsPerPage")}
                      ofLabel={translate("of")}
                    />
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: dt.cardBg,
                    borderRadius: dt.radiusLg,
                    border: `1px solid ${dt.cardBorder}`,
                    boxShadow: dt.cardShadow,
                    minHeight: "400px",
                    flex: 1,
                  }}
                >
                  <TableEmpty
                    iconComponent={BoxSearchIcon}
                    text={translate("noServiceRequestsFound")}
                    secondaryText={translate("createYourFirst")}
                  />
                </Box>
              )}
            </>
          )}

          {/* ═══════════════════════════════════════════════════
              CARD VIEW (phone only)
              ═══════════════════════════════════════════════════ */}
          {isPhone && (
            <>
              {allItems.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: dt.radiusSm,
                    backgroundColor: dt.cardBg,
                    border: `1px solid ${dt.cardBorder}`,
                    boxShadow: dt.cardShadow,
                    flex: 1,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      overflowX: "hidden",
                      padding: { xs: 1, md: 2 },
                      "&::-webkit-scrollbar": { width: "5px" },
                      "&::-webkit-scrollbar-track": { background: "transparent" },
                      "&::-webkit-scrollbar-thumb": { background: "#D1D5DB", borderRadius: "10px", "&:hover": { background: "#9CA3AF" } },
                      scrollbarWidth: "thin" as const,
                      scrollbarColor: "#D1D5DB transparent",
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
                      {pageItems.map((item, index) => (
                        <Box
                          key={item.id}
                          sx={changedIds.has(item.id) ? { animation: "rowHighlight 1.5s ease-out", borderRadius: "8px" } : undefined}
                        >
                          <MobileRequestCard
                            item={item}
                            translate={translate}
                            onView={handleView}
                            index={index}
                            preparingCount={presenceCounts[item.id] || 0}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      borderTop: `1px solid ${dt.cardBorder}`,
                      px: { xs: 1, md: 2 },
                      ...paginationWrapperSx,
                    }}
                  >
                    <LightTablePagination
                      totalCount={allItems.length}
                      currentPage={displayPageNo}
                      pageSize={displayPageSize}
                      pageSizeOptions={[10, 25, 50, 100]}
                      onPageChange={handlePageNoChange}
                      onPageSizeChange={handlePageSizeChange}
                      rowsPerPageLabel={translate("rowsPerPage")}
                      ofLabel={translate("of")}
                    />
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: dt.cardBg,
                    borderRadius: dt.radiusLg,
                    border: `1px solid ${dt.cardBorder}`,
                    boxShadow: dt.cardShadow,
                    minHeight: "300px",
                    flex: 1,
                  }}
                >
                  <TableEmpty
                    iconComponent={BoxSearchIcon}
                    text={translate("noServiceRequestsFound")}
                    secondaryText={translate("createYourFirst")}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════════
          PUBLISH SERVICE REQUEST DIALOG
          ═══════════════════════════════════════════════════════ */}
      <PublishServiceRequestDialog
        open={!!publishSR}
        onClose={() => setPublishSR(null)}
        onPublish={handlePublishConfirm}
      />

      {/* ═══════════════════════════════════════════════════════
          CONFIRM COMPLETION DIALOG (BUG 3)
          ═══════════════════════════════════════════════════════ */}
      <ConfirmCompletionDialog
        open={!!confirmCompletionSR}
        completion={completionData}
        files={confirmCompletionSR?.acceptedOffer?.files}
        onConfirm={handleConfirmCompletion}
        onRequestRevision={handleRequestRevision}
        onClose={() => {
          setConfirmCompletionSR(null);
          setCompletionData(null);
        }}
        loading={completionLoading}
      />

      {/* ═══════════════════════════════════════════════════════
          REQUEST REVISION DIALOG
          ═══════════════════════════════════════════════════════ */}
      <RequestRevisionDialog
        open={!!revisionSR}
        onSubmit={handleSubmitRevision}
        onClose={() => setRevisionSR(null)}
        loading={revisionLoading}
      />

      {/* ═══════════════════════════════════════════════════════
          LEAVE REVIEW DIALOG (BUG 3)
          ═══════════════════════════════════════════════════════ */}
      <LeaveReviewDialog
        open={!!reviewSR}
        revieweeCompanyName={reviewSR?.acceptedOffer?.providerName || "Service Provider"}
        onSubmit={handleSubmitReview}
        onSkip={() => setReviewSR(null)}
        onClose={() => setReviewSR(null)}
        loading={reviewLoading}
        error={reviewError}
      />

      {/* ═══════════════════════════════════════════════════════
          COMPARE OFFERS DIALOG
          ═══════════════════════════════════════════════════════ */}
      <CompareOffersDialog
        open={!!compareOffersSR}
        serviceRequestId={compareOffersSR?.id || ""}
        serviceRequestIssue={compareOffersSR?.issue || ""}
        onClose={() => setCompareOffersSR(null)}
        onOfferAccepted={() => {
          setCompareOffersSR(null);
          dispatch(getServiceRequests(apiQuery(serviceRequestsQuery)));
          dispatch(getServiceRequestsFilters({ search: serviceRequestsQuery.search }));
        }}
      />

    </Box>
  );
};

export default MyServiceRequests;
